export const INPUT_MODE_TEXT = 'text';
export const INPUT_MODE_VOICE = 'voice';

export const SENDER_USER = 'user';
export const SENDER_ASSISTANT = 'assistant';

/**
 * Converts a thread of messages into a chat format with user and assistant messages.
 * Reverses the thread order and flattens each message pair into separate user and assistant messages.
 * @param {Array} thread - Array of message pairs containing user and assistant responses.
 * @returns {Array} Array of formatted chat messages with sender, text, and timestamp.
 */
export const parseThreadToChat = (thread) =>
  thread.reverse().flatMap((messagePair) => {
    const { AssistantResponse, CreatedAt, UserMessage, RelevantSources } = messagePair;

    return [
      {
        sender: SENDER_USER,
        text: UserMessage || '',
        timestamp: new Date(CreatedAt),
      },
      {
        sender: SENDER_ASSISTANT,
        text: formatAssistantResponseWithCitations(AssistantResponse, RelevantSources),
        timestamp: new Date(CreatedAt),
      },
    ];
  });

/**
 * Formats assistant responses by inserting citation links at specified character indices.
 * Filters sources by relevance score and collects citations by their insertion points.
 * @param {string} response - The assistant's response text.
 * @param {Object} sources - Object containing search results with citations and metadata.
 * @returns {string} Formatted response with embedded citation links.
 */
export const formatAssistantResponseWithCitations = (response, sources) => {
  if (!response || !sources) return response || '';

  // Collect all citations with their indices, display names, URLs, and SourceID.
  const citationsByIndex = {};

  sources?.Results?.forEach(({ HighestScore, Source }) => {
    const { Citations, Metadata } = Source;
    if (HighestScore < 0.6 || !Citations || !Metadata) return;

    Citations.forEach(({ Index }) => {
      if (typeof Index !== 'number') return;
      if (!citationsByIndex[Index]) citationsByIndex[Index] = [];

      // Get the source metadata.
      const { SourceID, DisplayName, URL } = Metadata;

      // Deduplicate by SourceID.
      const alreadyExists = citationsByIndex[Index].some((c) => c.sourceId === SourceID);

      if (!alreadyExists) {
        citationsByIndex[Index].push({
          sourceId: SourceID,
          label: DisplayName,
          url: URL,
        });
      }
    });
  });

  // Sort indices descending to avoid messing up positions as we insert.
  const sortedIndices = Object.keys(citationsByIndex)
    .map(Number)
    .sort((a, b) => b - a);

  let formatted = response;

  sortedIndices.forEach((index) => {
    // Create citations with only citation ID.
    const links = citationsByIndex[index]
      .map((c, citationIndex) => {
        const citationId = `citation-${index}-${citationIndex}`;
        return `[${c.label}](${c.url} "data-citation-id:${citationId}")`;
      })
      .join(' ');
    // Insert the links at the index (after the character at that index).
    formatted = formatted.slice(0, index + 1) + ` ${links}` + formatted.slice(index + 1);
  });

  return formatted;
};

/**
 * Removes markdown formatting from text while preserving the content.
 * Converts markdown elements like headers, bold, italic, code blocks, links, and lists
 * to plain text for use in text-to-speech or other plain text contexts.
 * @param {string} markdownText - The markdown text to convert to plain text.
 * @returns {string} Plain text with all markdown formatting removed.
 */
export const stripMarkdown = (markdownText) => {
  if (!markdownText) return '';

  // Define markdown patterns to remove or transform
  const patterns = [
    // Headers (# ## ### etc.)
    { pattern: /^#{1,6}\s+/gm, replacement: '' },
    // Bold/italic markers (**text** or *text*)
    { pattern: /\*\*(.*?)\*\*/g, replacement: '$1' },
    { pattern: /\*(.*?)\*/g, replacement: '$1' },
    // Code blocks (```code```)
    { pattern: /```[\s\S]*?```/g, replacement: '' },
    // Inline code (`code`)
    { pattern: /`([^`]+)`/g, replacement: '$1' },
    // Images (![alt](url) -> alt)
    { pattern: /!\[([^\]]*)\]\([^)]+\)/g, replacement: '$1' },
    // Horizontal rules (--- or ***)
    { pattern: /^[-*_]{3,}$/gm, replacement: '' },
    // Blockquotes (> text)
    { pattern: /^>\s+/gm, replacement: '' },
    // Strikethrough (~~text~~)
    { pattern: /~~(.*?)~~/g, replacement: '$1' },
    // Citations with data-citation-id (remove completely).
    { pattern: /\[[^\]]*\]\([^)]*"data-citation-id:[^"]*"[^)]*\)/g, replacement: '' },
    // Regular hyperlinks (keep link text).
    { pattern: /\[([^\]]+)\]\([^)]+\)/g, replacement: '$1' },
  ];

  // Apply all patterns.
  let result = markdownText;
  patterns.forEach(({ pattern, replacement }) => {
    result = result.replace(pattern, replacement);
  });

  // Clean up whitespace and normalize.
  return result
    .replace(/\n\s*\n/g, '\n\n') // Normalize multiple newlines.
    .replace(/[ \t]+/g, ' ') // Normalize spaces and tabs within lines.
    .trim();
};
